import { Autocomplete, FormControlLabel, ListSubheader, Switch, TextField } from "@mui/material";
import { useGetList } from "react-admin";
import { Bundle } from "../providers/catalogProvider";
import { DrivingLicenseClass, DrivingLicenseClassEnum } from "../model/DrivingLicenseClass";
import { useState } from "react";

const bundleSegments = ["Regulär", "Wechsler", "Umschreiber", "Erweiterung", "Aufstieg", "ASF"] as const;

function getBundleSegment(bundle: Bundle): (typeof bundleSegments)[number] {
  if (bundle.salesCategory === "transcription") return "Umschreiber";
  if (bundle.salesCategory === "extension") return "Erweiterung";
  if (bundle.isDrivingLicenseClassAdvancement) return "Aufstieg";
  if (bundle.salesCategory === "asf-course") return "ASF";
  return bundle.isDrivingSchoolChange ? "Wechsler" : "Regulär";
}

export function BundleAutocomplete({
  drivingSchoolId,
  drivingLicenseClasses,
  onDrivingLicenseClassesChange,
  value,
  onChange,
  filterBundle,
}: {
  drivingSchoolId: string;
  drivingLicenseClasses: DrivingLicenseClass[];
  onDrivingLicenseClassesChange: (drivingLicenseClasses: DrivingLicenseClass[]) => void;
  value: Bundle | undefined;
  onChange: (bundle: Bundle | undefined) => void;
  filterBundle: (bundle: Bundle) => boolean;
}) {
  const [showBundlesWithoutBaseFee, setShowBundlesWithoutBaseFee] = useState(false);
  const normalizedDrivingLicenseClasses = [...drivingLicenseClasses].sort();
  const { data: bundles, isLoading } = useGetList<Bundle>(
    "bundles",
    {
      filter: {
        drivingSchoolId,
        disabled: false,
        drivingLicenseClasses: normalizedDrivingLicenseClasses,
      },
      sort: { field: "name", order: "ASC" },
      pagination: { page: 1, perPage: 9999 },
    },
    { enabled: normalizedDrivingLicenseClasses.length > 0 },
  );
  const options =
    bundles
      ?.filter(filterBundle)
      .filter((bundle) => showBundlesWithoutBaseFee || bundle.baseFee !== 0)
      .sort(
        (a, b) =>
          bundleSegments.indexOf(getBundleSegment(a)) - bundleSegments.indexOf(getBundleSegment(b)) ||
          a.name.localeCompare(b.name),
      ) ?? [];

  return (
    <>
      <FormControlLabel
        sx={{ mt: "1em" }}
        control={
          <Switch
            checked={showBundlesWithoutBaseFee}
            onChange={(_, checked) => {
              setShowBundlesWithoutBaseFee(checked);
              if (!checked && value?.baseFee === 0) {
                onChange(undefined);
              }
            }}
          />
        }
        label="Pakete mit Grundbetrag 0 € anzeigen"
      />
      <Autocomplete
        multiple
        sx={{ mt: "1em" }}
        options={DrivingLicenseClassEnum.options}
        value={drivingLicenseClasses}
        onChange={(_, selection) => {
          onDrivingLicenseClassesChange(selection);
          onChange(undefined);
        }}
        disableCloseOnSelect
        openOnFocus
        autoHighlight
        clearOnEscape
        openText="Öffnen"
        clearText="Löschen"
        closeText="Schließen"
        renderInput={(params) => <TextField {...params} label="Führerscheinklasse(n)" autoFocus />}
      />
      <Autocomplete
        sx={{ mt: "1em" }}
        options={options}
        getOptionLabel={(bundle) => bundle.name}
        getOptionKey={(bundle) => bundle.id}
        groupBy={getBundleSegment}
        renderGroup={({ key, group, children }) => (
          <>
            <ListSubheader key={`${key}-header`} sx={{ fontWeight: "bold", marginLeft: "-6px", lineHeight: "32px" }}>
              {group}:
            </ListSubheader>
            {children}
          </>
        )}
        value={value ?? null}
        onChange={(_, bundle) => onChange(bundle ?? undefined)}
        isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
        loading={isLoading}
        disabled={drivingLicenseClasses.length === 0}
        openOnFocus
        autoHighlight
        clearOnEscape
        noOptionsText={
          bundles && bundles.length > 0 && options.length === 0
            ? "Keine kompatiblen Pakete gefunden"
            : "Keine Pakete gefunden"
        }
        loadingText="Lädt..."
        openText="Öffnen"
        clearText="Löschen"
        closeText="Schließen"
        renderInput={(params) => <TextField {...params} label="Paket suchen" />}
      />
    </>
  );
}
